import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, apiClient } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import { Download, Trash2, ShieldCheck, FileText, Loader2 } from "lucide-react";
import ReferralCard from "@/components/ReferralCard";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Settings() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient().get("/user/audit-log").then((r) => setAudit(r.data)).catch(() => {});
  }, []);

  const exportData = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient().get("/user/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `setu-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been exported.");
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await apiClient().post("/user/delete");
      toast.success("Account deleted. Goodbye.");
      await logout();
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 fade-in-up max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-5xl mt-1">{t("settings")}</h1>
      </div>

      <div className="border border-border bg-card p-5 space-y-4">
        <div className="font-heading text-lg">Profile</div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Name</div><div>{user?.name}</div></div>
          <div><div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Email</div><div>{user?.email}</div></div>
          <div><div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Company</div><div>{user?.company || "—"}</div></div>
          <div><div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Role</div><div>{user?.role}</div></div>
          <div><div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Auth</div><div>{user?.auth_provider}</div></div>
          <div><div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Plan</div><div className="text-primary">{user?.subscription?.plan?.toUpperCase() || "FREE"}</div></div>
        </div>
      </div>

      <div className="border border-border bg-card p-5 space-y-4">
        <div className="font-heading text-lg">Appearance & Language</div>
        <div className="flex flex-wrap items-center gap-3">
          <ThemeToggle />
          <LanguageSwitcher />
        </div>
      </div>

      <ReferralCard />

      <div className="border border-border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <div className="font-heading text-lg">Your Data Rights</div>
        </div>
        <p className="text-sm text-muted-foreground">
          Under India's DPDPA 2023 and GDPR, you have the right to access, export, and delete your data. Read our{" "}
          <Link to="/privacy" className="text-primary underline">Privacy Policy</Link>.
        </p>
        <div className="flex flex-wrap gap-2">
          <button onClick={exportData} disabled={busy} data-testid="export-data-btn"
            className="btn-sharp inline-flex items-center gap-2 border border-border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export my data (JSON)
          </button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="delete-account-btn"
                className="btn-sharp inline-flex items-center gap-2 border border-destructive/50 text-destructive px-4 py-2 text-sm hover:bg-destructive/10">
                <Trash2 className="h-4 w-4" /> Delete my account
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-account-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete account permanently?</AlertDialogTitle>
                <AlertDialogDescription>
                  All your projects, incidents, AI outputs, workers, attendance records, and voice notes will be permanently removed. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deleteAccount} data-testid="delete-confirm"
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Yes, delete everything
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="border border-border bg-card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <div className="font-heading text-lg">Activity Log</div>
        </div>
        <p className="text-xs text-muted-foreground">Recent security-relevant events on your account.</p>
        {audit.length === 0 ? (
          <div className="text-sm text-muted-foreground">No activity yet.</div>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {audit.slice(0, 20).map((a) => (
              <li key={a.id} className="border border-border p-2 flex items-center justify-between text-xs" data-testid={`audit-${a.id}`}>
                <span className="mono">{a.action}</span>
                <span className="mono text-muted-foreground">{new Date(a.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
